import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(context: {
  supabase: any;
  userId: string;
}) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

// PUBLIC: list active attachments (id/kind/label only) so contract forms
// can offer checkboxes. No storage paths or URLs leak.
export const listPublicCompanyAttachments = createServerFn({ method: "GET" })
  .handler(async () => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("company_attachments")
      .select("id, kind, label, sort_order")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return { attachments: data ?? [] };
  });

// ADMIN: list everything (active + inactive)
export const adminListCompanyAttachments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("company_attachments")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { attachments: data ?? [] };
  });

export const recordCompanyAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        kind: z.string().trim().min(2).max(40),
        label: z.string().trim().min(2).max(120),
        storage_path: z.string().min(3).max(500),
        original_filename: z.string().min(1).max(255),
        size_bytes: z.number().int().nonnegative().max(50 * 1024 * 1024),
        mime_type: z.string().max(120).optional().default(""),
        sort_order: z.number().int().min(0).max(999).optional().default(0),
        version: z
          .string()
          .trim()
          .regex(/^v?\d+(\.\d+){0,2}$/i, "version must look like v1.0")
          .max(20)
          .optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const normalizedVersion = data.version
      ? data.version.toLowerCase().startsWith("v")
        ? data.version
        : `v${data.version}`
      : undefined;
    const { error } = await supabaseAdmin.from("company_attachments").insert({
      kind: data.kind,
      label: data.label,
      storage_path: data.storage_path,
      original_filename: data.original_filename,
      size_bytes: data.size_bytes,
      mime_type: data.mime_type,
      sort_order: data.sort_order,
      uploaded_by: context.userId,
      ...(normalizedVersion ? { version: normalizedVersion } : {}),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const toggleCompanyAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ id: z.string().uuid(), is_active: z.boolean() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin
      .from("company_attachments")
      .update({ is_active: data.is_active })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteCompanyAttachment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: row, error: getErr } = await supabaseAdmin
      .from("company_attachments")
      .select("storage_path")
      .eq("id", data.id)
      .maybeSingle();
    if (getErr) throw new Error(getErr.message);
    if (row?.storage_path) {
      await supabaseAdmin.storage
        .from("company-assets")
        .remove([row.storage_path]);
    }
    const { error } = await supabaseAdmin
      .from("company_attachments")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ADMIN: issue a short-lived signed URL so admins can preview/download
// any company asset (active or inactive). Forces attachment download when
// requested.
export const adminGetCompanyAttachmentUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        download: z.boolean().optional().default(false),
        expires_in: z.number().int().min(30).max(3600).optional().default(600),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: row, error: getErr } = await supabaseAdmin
      .from("company_attachments")
      .select("storage_path, original_filename")
      .eq("id", data.id)
      .maybeSingle();
    if (getErr) throw new Error(getErr.message);
    if (!row?.storage_path) throw new Error("Asset not found");
    const { data: signed, error } = await supabaseAdmin.storage
      .from("company-assets")
      .createSignedUrl(
        row.storage_path,
        data.expires_in,
        data.download
          ? { download: row.original_filename ?? true }
          : undefined,
      );
    if (error || !signed?.signedUrl) {
      throw new Error(error?.message ?? "Failed to sign URL");
    }
    return {
      url: signed.signedUrl,
      filename: row.original_filename ?? "asset.pdf",
    };
  });

